import { shouldShowAssignStatusForManager } from './leadAssignUserHelpers';

export const getStatusColor = (s) => {
  const map = {
    New: 'primary',
    Contacted: 'info',
    Qualified: 'success',
    Connected: 'warning',
    Lost: 'error',
    'Follow up': 'secondary',
    Chatting: 'info',
    Select: 'default',
    Meeting: 'warning',
    Assigned: 'info',
  };
  return map[s] || 'default';
};

export const formatFileName = (file, index) => {
  return file?.title || file?.file_name || file?.name || `File ${index + 1}`;
};

export const formatFileType = (file, filePath) => {
  return (file?.type || file?.mime_type || filePath?.split('.').pop())?.toUpperCase();
};

export function normalizeStatusLabel(name) {
  return (name || '').toLowerCase().replace(/[\s_-]+/g, ' ').trim();
}

export function findStatusByNameMatch(statusOptions, predicate) {
  if (!Array.isArray(statusOptions)) return null;
  return statusOptions.find((s) => predicate(normalizeStatusLabel(s?.name))) ?? null;
}

export function findHoldIuwStatus(statusOptions) {
  return findStatusByNameMatch(statusOptions, (name) => name.includes('hold iuw'));
}

export function findResolvedIuwStatus(statusOptions) {
  return findStatusByNameMatch(
    statusOptions,
    (name) => name.includes('resolved iuw') || name.includes('resolve iuw')
  );
}

export function isCloserUser(user) {
  if (!user) return false;
  const roleNames = [];
  if (user.role?.name) roleNames.push(user.role.name);
  if (typeof user.role === 'string') roleNames.push(user.role);
  if (Array.isArray(user.roles)) {
    user.roles.forEach((role) => {
      roleNames.push(role?.name || role);
    });
  }
  return roleNames.some((name) => String(name || '').toLowerCase().includes('closer'));
}

export function getUserPrimaryRoleName(user) {
  return (user?.roles?.[0]?.name || user?.role?.name || '').toLowerCase().trim();
}

export function isQcOrQcManagerRole(userOrRoleName) {
  const name =
    typeof userOrRoleName === 'string'
      ? userOrRoleName.toLowerCase().trim()
      : getUserPrimaryRoleName(userOrRoleName);
  return name === 'qc' || name === 'qc manager';
}

export function isQcApprovedOrDeclinedStatus(statusName) {
  const n = normalizeStatusLabel(statusName);
  return n === 'qc approved' || n === 'qc declined';
}

/** Lead is locked for edit/status/assign (API flag, QC decision, or manager assigned away). */
export function isLeadDisabledLike(lead, userOrRoleName) {
  if (lead?.is_disabled) return true;
  // Manager assigned lead to another user → lock on manager view
  if (typeof userOrRoleName !== 'string' && shouldShowAssignStatusForManager(lead, userOrRoleName)) {
    return true;
  }
  if (!isQcOrQcManagerRole(userOrRoleName)) return false;
  const qcLogName = lead?.lead_q_c_log?.status?.name;
  const lastStatusName = lead?.last_status_log?.status?.name;
  return (
    isQcApprovedOrDeclinedStatus(qcLogName) ||
    isQcApprovedOrDeclinedStatus(lastStatusName)
  );
}

export function isAttachmentRejected(file) {
  return String(file?.status || '').toLowerCase() === 'rejected';
}

export function getAttachmentRejectReason(file) {
  const value = file?.comment ?? file?.reject_reason ?? file?.rejection_reason ?? file?.query_comment;
  return value != null && value !== '' ? String(value) : '';
}

export function isAttachmentVerified(file) {
  return String(file?.status || '').toLowerCase() === 'resolved';
}

export function isAttachmentRequired(file) {
  return !isAttachmentRejected(file) && !isAttachmentVerified(file) && !file?.path;
}

export function getAttachmentStatusMeta(file) {
  if (isAttachmentRejected(file)) {
    return { label: 'Rejected', color: 'error' };
  }
  if (isAttachmentVerified(file)) {
    return { label: 'Resolved', color: 'success' };
  }
  if (isAttachmentRequired(file)) {
    return { label: 'Required from Client', color: 'warning' };
  }
  return { label: 'Pending Review', color: 'info' };
}

export function getAttachmentDocumentFor(file) {
  const value =
    file?.title ??
    file?.document_for ??
    file?.documentFor ??
    file?.for_document ??
    file?.doc_for;
  if (value == null || value === '') return '—';
  return String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/** Eager-loaded relations for GET /leads — single source of truth for list + autocomplete */
export const LEADS_LIST_RELATIONS = [
  'source',
  'assignees',
  'dba',
  'author',
  'lastStatusLog',
  'attachments',
  'meetings',
  'issue',
  'country',
  'leadQCLog',
];

/**
 * Params for lead pickers (e.g. call recordings). Matches lead list API: same relations, sort, paginate.
 */
export function buildLeadsAutocompleteParams({
  search = '',
  page = 1,
  perPage = 100,
  sortBy = 'updated_at',
  sortDirection = 'desc',
} = {}) {
  const term = typeof search === 'string' ? search.trim() : '';
  return {
    'relations[]': LEADS_LIST_RELATIONS,
    paginate: 1,
    page,
    per_page: perPage,
    sort_by: sortBy,
    sort_direction: sortDirection,
    ...(term ? { search: term } : {}),
  };
}

export const buildQueryParams = (pagination, filters, sort, noActivity, searchValue) => {
  const params = {
    'relations[]': LEADS_LIST_RELATIONS,
    paginate: 1,
    page: pagination.page + 1,
    per_page: pagination.pageSize,
    ...(searchValue && { search: searchValue }),
  };

  // Status is a top-level query param (not filters[status_id])
  if (filters.status_id && filters.status_id !== '') {
    params.status_id = filters.status_id;
  }

  // Date range is top-level: date_from / date_to (YYYY-MM-DD)
  if (filters.date_from && filters.date_from !== '') {
    params.date_from = filters.date_from;
  }
  if (filters.date_to && filters.date_to !== '') {
    params.date_to = filters.date_to;
  }

  const otherFilters = { ...filters };
  delete otherFilters.status_id;
  delete otherFilters.status_name;
  delete otherFilters.date_from;
  delete otherFilters.date_to;

  Object.entries(otherFilters).forEach(([key, value]) => {
    if (value && value !== '') {
      params[`filters[${key}]`] = value;
    }
  });

  if (sort.sort_by) params['sort_by'] = sort.sort_by;
  if (sort.sort_direction) params['sort_direction'] = sort.sort_direction;
  if (noActivity.no_activity_since_days) {
    params['no_activity_since_days'] = noActivity.no_activity_since_days;
  }

  return params;
};